/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import React, { createContext, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import {
  API_URL,
  ERROR_MESSAGE,
  ADMIN_USER_TYPE,
  ADMIN_USER_ID,
  ADMIN_USER_FULL_NAME,
  ADMIN_PERMISSION_KEY,
  ADMIN_JWT_TOKEN,
  ADMIN_PREFERRED_NAME,
  ADMIN_REF_URL,
  ADMIN_ACCESS_CODE,
  ADMIN_COMPANY_DATA,
  ADMIN_UNIQUE_ID,
  CLIENT_ENFORCE_UPDATE,
  ADMIN_MAIN_PROFILE_USER_ID,
  ADMIN_MAIN_PROFILE_FULL_NAME,
  ADMIN_USER_PATHS,
  ADMIN_SUPERVISOR_PROFILE_FULL_NAME,
  ADMIN_SUPERVISOR_PROFILE_USER_ID,
  ADMIN_ENFORCE_PASSWORD,
  ADMIN_ENCRYPTION_KEY,
  ADMIN_COUNTRY_CODE,
  ADMIN_SELECTED_LANGUAGE,
  ADMIN_USER_EMAIL,
  ADMIN_SUPPORT_DATA,
} from '../../../constants/appConfig';
import {
  clearLocalStorage,
  getLocalStorage,
  setLocalStorage,
  setLocalStorageBulk,
} from '../../../utils/storageUtil';
import { isAdminAuthenticated } from '../../../utils/jwtUtil';
import history from '../../../utils/history';
import {
  adminAppAskForPermissionToReceiveNotifications,
  adminAppUnregisterFirebase,
} from '../../../pushnotification';
import {
  adminAppHandleFirebaseProfileLoginOrCreation,
  getCurrentTimeZone,
  isBrowserSafari,
  isEmpty,
} from '../../../utils/commonUtil';
import { adminLoginLocalStorage, adminLogoutLocalStorage } from '../../../utils/authUtil';
import { isFirebaseSupported } from '../../../FirebaseAdminConfig';
import { fetchFailReportSentConfigurationForAdmin } from '../../../pages/duck/accounting/service';

const AdminAuthContext = createContext({});

const AdminAuthProvider = props => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(getLocalStorage('user') || {});
  const [loading, setLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [message, setMessage] = useState({});
  const [fields, setFields] = useState({
    userId: '',
    password: '',
    accessCode: '',
  });
  const [sideBarMenu, setSideBarMenu] = useState('users');
  const [primaryColor, setPrimaryColor] = useState('#466FEE');
  const [secondaryColor, setSecondaryColor] = useState('#3CD4A0');
  const [selectedFilterDate, setSelectedFilterDate] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadChatRooms, setUnreadChatRooms] = useState([]);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);

  // Admin App
  const [adminUserFullName, setUserAdminFullName] = useState(
    getLocalStorage(ADMIN_USER_FULL_NAME) || {}
  );
  const [adminUserId, setAdminUserId] = useState(getLocalStorage(ADMIN_USER_ID) || {});
  const [adminUserType, setAdminUserType] = useState(getLocalStorage(ADMIN_USER_TYPE) || {});
  const [adminSupportData, setAdminSupportData] = useState(
    getLocalStorage(ADMIN_SUPPORT_DATA) || {}
  );

  const [adminAuthenticated, setAdminAuthenticated] = useState(isAdminAuthenticated() || false);
  const [adminAccessCode, setAdminAccessCode] = useState(getLocalStorage(ADMIN_ACCESS_CODE));
  const [adminCompanyData, setAdminCompanyData] = useState(getLocalStorage(ADMIN_COMPANY_DATA));
  const [enforceUpdateStatus, setEnforceUpdateStatus] = useState(
    getLocalStorage(CLIENT_ENFORCE_UPDATE) || false
  );
  const [mainProfileFullName, setMainProfileFullName] = useState(
    getLocalStorage(ADMIN_MAIN_PROFILE_FULL_NAME) || undefined
  );

  const [mainProfileUserId, setMainProfileUserId] = useState(
    getLocalStorage(ADMIN_MAIN_PROFILE_USER_ID) || undefined
  );

  const [supervisorProfileFullName, setSupervisorProfileFullName] = useState(
    getLocalStorage(ADMIN_SUPERVISOR_PROFILE_FULL_NAME) || undefined
  );

  const [supervisorProfileUserId, setSupervisorProfileUserId] = useState(
    getLocalStorage(ADMIN_SUPERVISOR_PROFILE_USER_ID) || undefined
  );

  const [profileSwitchPaths, setProfileSwitchPaths] = useState(
    getLocalStorage(ADMIN_USER_PATHS) ? getLocalStorage(ADMIN_USER_PATHS) : []
  );
  const [adminEncryptedKey, setAdminEncryptedKey] = useState(
    getLocalStorage(ADMIN_ENCRYPTION_KEY || {})
  );
  const [chatRooms, setChatRooms] = useState([]);
  const [quickbookURL, setQuickbookURL] = useState();
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [csvDownloadLoader, setCsvDownloadLoader] = useState(false);
  const [csvDownloadFilename, setCsvDownloadFilename] = useState([]);
  const [adminCountryCode, setAdminCountryCode] = useState(
    getLocalStorage(ADMIN_COUNTRY_CODE) || ''
  );
  const [adminSelectedLanguage, setAdminSelectedLanguage] = useState(
    getLocalStorage(ADMIN_SELECTED_LANGUAGE) || 'en'
  );

  const [clientStatusChangeError, setClientStatusChangeError] = useState({ message: '' });

  const [adminUserEmail, setAdminUserEmail] = useState(getLocalStorage(ADMIN_USER_EMAIL) || {});
  const [reportDownloadLoader, setReportDownloadLoader] = useState(false);

  const state = {
    user,
    loading,
    adminAuthenticated,
    adminUserType,
    enforceUpdateStatus,
    adminUserId,
    adminUserFullName,
    adminAccessCode,
    adminCompanyData,
    companyLoading,
    mainProfileFullName,
    mainProfileUserId,
    profileSwitchPaths,
    supervisorProfileFullName,
    supervisorProfileUserId,
    selectedFilterDate,
    chatRooms,
    unreadMessageCount,
    adminEncryptedKey,
    unreadChatRooms,
    quickbookURL,
    downloadModalVisible,
    csvDownloadLoader,
    csvDownloadFilename,
    adminCountryCode,
    unreadSupportCount,
    adminSelectedLanguage,
    clientStatusChangeError,
    adminUserEmail,
    adminSupportData,
    reportDownloadLoader,
  };

  const redirectToDashboard = () => {
    history.push('/admin/dashboard');
  };

  const cleanAdminAuthContextClean = () => {
    setLoading(false);
    setCompanyLoading(false);
    setMessage(undefined);
    setFields({ userId: '', password: '', accessCode: '' });
  };

  const adminRegister = firebaseReceivedToken => {
    return axios
      .create({
        baseURL: `${API_URL}`,
        headers: {
          'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
          'X-TIME-ZONE': getCurrentTimeZone(),
          'client-device': 'web',
        },
      })
      .put('/atom-manager/v1/notifications/register/token', {
        token: firebaseReceivedToken,
      })
      .then(response => {
        if (response.status === 200) {
          return response?.data;
        }
      })
      .catch(error => {
        console.info('Firebase::', error?.response?.data?.message);
      });
  };

  const setMainProfileData = responseData => {
    setLocalStorageBulk([
      { key: ADMIN_MAIN_PROFILE_FULL_NAME, value: responseData.fullName },
      { key: ADMIN_MAIN_PROFILE_USER_ID, value: responseData.userId },
    ]);
    setMainProfileFullName(responseData.fullName);
    setMainProfileUserId(responseData.userId);
  };

  const setSupervisorProfileData = data => {
    setLocalStorageBulk([
      { key: ADMIN_SUPERVISOR_PROFILE_FULL_NAME, value: data?.fullName },
      { key: ADMIN_SUPERVISOR_PROFILE_USER_ID, value: data?.userName },
    ]);
    setSupervisorProfileFullName(data?.fullName);
    setSupervisorProfileUserId(data?.userName);
  };

  const adminLoginSuccess = (responseData, accessCode) => {
    adminLoginLocalStorage(responseData, accessCode);

    clearLocalStorage(ADMIN_SUPERVISOR_PROFILE_FULL_NAME);
    clearLocalStorage(ADMIN_SUPERVISOR_PROFILE_USER_ID);
    setSupervisorProfileFullName(undefined);
    setSupervisorProfileUserId(undefined);
    if (responseData?.supervisorInfo) {
      setSupervisorProfileData(responseData?.supervisorInfo);
    }

    setSelectedFilterDate([]);
    setUserAdminFullName(responseData.fullName);
    setAdminUserId(responseData.userId);
    setAdminSupportData({
      supportOrganizationId: responseData.supportOrganizationId,
      loginUserSupportId: responseData.loginUserSupportId,
      defaultUserSupportId: responseData.defaultUserSupportId,
    });
    setAdminUserEmail(responseData?.email);
    setAdminUserType('ADMIN');
    setMessage(undefined);
    setAdminAuthenticated(true);
    setSideBarMenu('client');
    setAdminAccessCode(responseData?.accessCode);
    setUnreadMessageCount(0);
    setAdminEncryptedKey(responseData?.encryptionKey);
    setAdminCountryCode(responseData?.countryCode);
    setAdminSelectedLanguage(responseData?.userPreferences?.locale?.languageCode); // This code is needed for user preference of multi lingual //
    setUnreadSupportCount(0);
    if (responseData?.accessCode) {
      dispatch(fetchFailReportSentConfigurationForAdmin(responseData.accessCode));
    }

    setTimeout(() => {
      if (isEmpty(getLocalStorage(ADMIN_USER_PATHS))) {
        setProfileSwitchPaths([]);
        setProfileSwitchPaths([
          {
            title: responseData.fullName,
            code: responseData.userId,
          },
        ]);
        setLocalStorage(ADMIN_USER_PATHS, [
          {
            title: responseData.fullName,
            code: responseData.userId,
          },
        ]);
      }

      if (isEmpty(getLocalStorage(ADMIN_MAIN_PROFILE_USER_ID))) {
        setMainProfileData(responseData);
      }

      setLoading(false);

      if (responseData?.enforcePasswordChange) {
        setLocalStorage(ADMIN_ENFORCE_PASSWORD, true);
        history.push('/admin/enforcepassword');
      } else {
        if (getLocalStorage(ADMIN_ENFORCE_PASSWORD)) {
          clearLocalStorage(ADMIN_ENFORCE_PASSWORD);
        }

        if (history?.location?.search) {
          const searchParams = history?.location?.search;
          const [_, param1, param2, param3] = searchParams?.split('=');

          if (param1 && param2 && param3) {
            const url = `/${param1}=${param2}=${param3}`;
            history.push(url);
          } else {
            redirectToDashboard();
          }
        } else {
          redirectToDashboard();
        }
      }

      if (!isBrowserSafari() && isFirebaseSupported()) {
        adminAppAskForPermissionToReceiveNotifications().then(token => {
          if (token) {
            adminRegister(token);
          }
        });
      }
    }, 1000);
  };

  const adminLogin = ({ userId, password, accessCode }) => {
    setLoading(true);
    return axios
      .post(
        API_URL + '/atom-manager/v1/auths',
        {
          userId,
          password,
          accessCode,
        },
        {
          headers: {
            'X-TIME-ZONE': getCurrentTimeZone(),
            'client-device': 'web',
          },
        }
      )
      .then(response => {
        if (response?.status === 200) {
          adminLoginSuccess(response?.data?.data, accessCode);
          localStorage.removeItem('COMPANY_UNAUTHORIZED');
          return response?.data;
        }
      })
      .catch(error => {
        setLoading(false);
        setMessage(error?.response?.data ? error.response.data : ERROR_MESSAGE);
      });
  };

  const handleUserSwitchPaths = data => {
    let newPath = {
      title: data?.fullName,
      code: data?.userId,
    };

    if (isEmpty(getLocalStorage(ADMIN_USER_PATHS)?.filter(path => path?.code === newPath?.code))) {
      setProfileSwitchPaths([...getLocalStorage(ADMIN_USER_PATHS), newPath]);
      setLocalStorage(ADMIN_USER_PATHS, [...getLocalStorage(ADMIN_USER_PATHS), newPath]);
    } else {
      let indexOfExistingItem = profileSwitchPaths?.map(path => path?.code)?.indexOf(newPath?.code);
      setProfileSwitchPaths(
        profileSwitchPaths?.filter((path, index) => index <= indexOfExistingItem)
      );
      setLocalStorage(
        ADMIN_USER_PATHS,
        profileSwitchPaths?.filter((path, index) => index <= indexOfExistingItem)
      );
    }

    localStorage.setItem('change-user-event-admin', 'change' + Math.random());
  };

  const handleUserProfileChange = data => {
    clearLocalStorage(ADMIN_JWT_TOKEN);
    clearLocalStorage(ADMIN_PERMISSION_KEY);
    clearLocalStorage(ADMIN_USER_FULL_NAME);
    clearLocalStorage(ADMIN_USER_ID);
    clearLocalStorage(ADMIN_USER_TYPE);
    clearLocalStorage(ADMIN_SUPPORT_DATA);
    clearLocalStorage(ADMIN_PREFERRED_NAME);
    clearLocalStorage(ADMIN_REF_URL);
    clearLocalStorage(ADMIN_UNIQUE_ID);
    clearLocalStorage(ADMIN_USER_EMAIL);

    adminLoginSuccess(data, data?.accessCode);
    var accessCode = data?.accessCode?.toString()?.toLowerCase();
    var firebaseEmail = `${data?.accessCode}_${data?.email}`.toLowerCase();
    var firebasePassword = data?.firebaseKey;
    adminAppHandleFirebaseProfileLoginOrCreation(accessCode, firebaseEmail, firebasePassword);

    handleUserSwitchPaths(data);
  };

  const adminCompanyDataClean = () => {
    setAdminCompanyData({});
    setLocalStorage(ADMIN_COMPANY_DATA, {});
  };

  const adminCompanyDataFetch = accessCode => {
    setCompanyLoading(true);
    return axios
      .get(API_URL + `/atom-client/v1/company/public/${accessCode}/company-detail`, {
        headers: {
          'X-TIME-ZONE': getCurrentTimeZone(),
        },
      })
      .then(response => {
        setCompanyLoading(false);
        if (response.status === 200) {
          setAdminCompanyData(response.data.data);
          setLocalStorage(ADMIN_COMPANY_DATA, response.data.data);
          return response.data;
        }
      })
      .catch(error => {
        setCompanyLoading(false);
        setAdminCompanyData(error.response ? error.response.data.message : ERROR_MESSAGE);
      });
  };

  const adminLogout = () => {
    adminLogoutLocalStorage();

    setSelectedFilterDate([]);
    setSupervisorProfileFullName(undefined);
    setSupervisorProfileUserId(undefined);
    setMainProfileFullName(undefined);
    setMainProfileUserId(undefined);
    setAdminUserType(undefined);
    setProfileSwitchPaths([]);
    setUser({});
    setAdminAuthenticated(false);
    adminCompanyDataClean();
    setFields({ userId: '', password: '', accessCode: '' });
    setAdminEncryptedKey({});
    setAdminCountryCode(undefined);
    setAdminSelectedLanguage('en');

    // history.push(adminAccessCode ? `/${adminAccessCode}/admin/login` : `/admin/login`);
    adminAppUnregisterFirebase();

    // dispatch({
    //   type: 'LOG_OUT_SUCCESS',
    // });
  };

  return (
    <AdminAuthContext.Provider
      {...props}
      value={{
        ...state,
        message: message,
        setMessage: setMessage,
        loading: loading,
        fields: fields,
        setLoading: setLoading,
        sideBarMenu: sideBarMenu,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor,
        adminCompanyDataClean: adminCompanyDataClean,
        adminCompanyDataFetch: adminCompanyDataFetch,
        adminLogin: adminLogin,
        adminLogout: adminLogout,
        setEnforceUpdateStatus: setEnforceUpdateStatus,
        setFields: setFields,
        setPrimaryColor: setPrimaryColor,
        setSecondaryColor: setSecondaryColor,
        cleanAdminAuthContextClean: cleanAdminAuthContextClean,
        adminSwitchProfileData: handleUserProfileChange,
        setProfileSwitchPaths: setProfileSwitchPaths,
        setSelectedFilterDate: setSelectedFilterDate,
        setChatRooms: setChatRooms,
        setUnreadMessageCount: setUnreadMessageCount,
        setUnreadChatRooms: setUnreadChatRooms,
        setQuickbookURL: setQuickbookURL,
        setDownloadModalVisible: setDownloadModalVisible,
        setCsvDownloadLoader: setCsvDownloadLoader,
        setCsvDownloadFilename: setCsvDownloadFilename,
        setUnreadSupportCount: setUnreadSupportCount,
        setAdminSelectedLanguage: setAdminSelectedLanguage,
        setClientStatusChangeError: setClientStatusChangeError,
        setReportDownloadLoader: setReportDownloadLoader,
      }}
    >
      {props.children}
    </AdminAuthContext.Provider>
  );
};

export { AdminAuthContext, AdminAuthProvider };
